import { useEffect, useState } from 'react'

import { basePaths } from '~/app/routes'
import { Schemas } from '~/clients/api-client'
import { OperationalPlannerFilter } from '~/store/slices/operationalPlannerFilterSlice'
import { SurgeonSchedulerFilter } from '~/store/slices/surgeonSchedulerFilterSlice'
import { SurgerySchedulerFilter } from '~/store/slices/surgerySchedulerFilterSlice'
import { VisibilityOption, VisibilityOptions } from '~/store/slices/types'
import { WaitingListFilter } from '~/store/slices/waitingListFilterSlice'
import { trackUserEvent } from '~/utils/analytics'
import { FilterProfileCountMap, ViewOptionProfleData } from '~/utils/analytics/helpers/dataTypes'
import { UserEventKey } from '~/utils/analytics/userEvents'

import { useFiltersByPathname } from './filterMigration/useFiltersByPathname'
import { useListViewOptionProfilesQuery } from './tanstack-query/baseClient/viewOptionProfiles/useListViewOptionProfilesQuery'
import { useSelectViewOptionProfileMutation } from './tanstack-query/baseClient/viewOptionProfiles/useSelectViewOptionProfileMutation'

export type AnyFilter = OperationalPlannerFilter | WaitingListFilter | SurgerySchedulerFilter | SurgeonSchedulerFilter

type ViewOptionProfileEventKey = Extract<
    UserEventKey,
    'CREATED_VIEW_OPTION_PROFILE' | 'UPDATED_VIEW_OPTION_PROFILE' | 'SELECTED_VIEW_OPTION_PROFILE' | 'DELETED_VIEW_OPTION_PROFILE'
>

// these are the pages that have filters associated with them
export type BaseRoutesWithFilters = 'patients' | 'or-schedule' | 'surgeon-scheduler' | 'surgery-scheduler'
export const getPageTypeByPathname = (pathname: string): BaseRoutesWithFilters => {
    if (pathname.startsWith(basePaths.PATIENTS)) return 'patients'
    if (pathname.startsWith(basePaths.OR_SCHEDULE)) return 'or-schedule'
    if (pathname.startsWith(basePaths.SURGEON_SCHEDULER)) return 'surgeon-scheduler'
    if (pathname.startsWith(basePaths.SURGERY_SCHEDULER)) return 'surgery-scheduler'
    throw new Error(`No route found for pathname: ${pathname}`)
}

/**
 * Function to determine if the provided filters match those defined in the view option profile.
 *
 * @param profile The view option profile to compare against.
 * @param filters A list of currently applied filters.
 *
 * @return boolean indicating whether the applied filters match the profile's filter settings.
 */
export function isMatchingProfileFilters(profile: Schemas['ViewOptionProfileOut'] | undefined, filters: AnyFilter[]) {
    const filterProfile = profile?.filter_profile

    if (!filterProfile) return false

    // Check each filter type in the profile
    // DOES not handle filters that differ on the same root path but are in different tabs
    for (const [type, values] of Object.entries(filterProfile)) {
        const appliedFilterValues = filters.filter(filter => filter.type === type).map(filter => filter.value)
        const profileFilterValues = Array.isArray(values) ? values : []

        // No need to check further if counts differ
        if (profileFilterValues.length !== appliedFilterValues.length) return false

        // Check if all applied filter values matches profile values
        if (!profileFilterValues.every(value => appliedFilterValues.includes(value as string))) return false
    }

    return true
}

export function isMatchingProfileVisibilityOptions<T extends string>(
    profile: Schemas['ViewOptionProfileOut'] | undefined,
    visibilityOptions: VisibilityOptions<T>
) {
    const options = Object.entries(visibilityOptions) as [T, VisibilityOption][]
    const viewProfile = (profile?.view_profile ?? {}) as Record<T, boolean>

    for (const [key, option] of options) {
        // Default to true if option is not specified in profile
        const profileValue = key in viewProfile ? viewProfile[key] : true

        // Profile mismatch if any visibility option differs
        if (profileValue !== option.value) return false
    }

    return true
}

/**
 * Mapper function that returns the number of filters applied for each filter type, rather than their values
 *
 * @param filterProfile - the filter profile to map (if any)
 * @returns a record with filter types as keys and the number of applied filters as value
 */
function toFilterProfileCount(filterProfile?: Schemas['ViewOptionProfileOut']['filter_profile']): FilterProfileCountMap {
    if (!filterProfile) return {}

    // If the filter value is not an array, it is considered a singular value
    return Object.entries(filterProfile).map(([name, value]) => [name, Array.isArray(value) ? value.length : 1])
}

/**
 * Mapper function that omits values that are considered either sensitive or irrelevant for user events.
 *
 * @param filterProfile - the view option profile to map (if any)
 * @returns an ViewOptionProfleData object, used for event tracking
 */
function toViewOptionProfleData(profile?: Schemas['ViewOptionProfileOut']): ViewOptionProfleData | undefined {
    if (!profile) return undefined

    const { view_profile, page_type, filter_profile } = profile

    return {
        view_profile,
        page_type,
        filter_profile: toFilterProfileCount(filter_profile),
    }
}

export function trackViewOptionProfileUserEvent(key: ViewOptionProfileEventKey, profile?: Schemas['ViewOptionProfileOut']) {
    void trackUserEvent(key, toViewOptionProfleData(profile))
}

export type { FilterHookResult } from './filterMigration/useFiltersByPathname'

/**
 * Hook for managing view option profiles for a given path, including selection and optimistic updates.
 *
 * @param path the current page path to fetch relevant view option profiles for.
 * @returns an object containing the list of profiles, the currently selected profile, and a function to set the selected profile.
 */
export const useViewOptionProfile = (path: string) => {
    const { applyFilterProfile } = useFiltersByPathname(path)

    const selectViewOptionProfile = useSelectViewOptionProfileMutation()
    const { data } = useListViewOptionProfilesQuery()

    const serverViewOptionProfiles = data?.filter(profile => profile.page_type === getPageTypeByPathname(path))

    // Track if we have a pending mutation to prevent race conditions
    const [pendingProfileId, setPendingProfileId] = useState<number | null>(null)

    // Allows us to optimistically update the selected profile in the UI
    const [optimisticallySelectedProfile, setOptimisticallySelectedProfile] = useState<
        (Schemas['ViewOptionProfileOut'] & { is_optimistic?: boolean }) | undefined
    >(undefined)

    useEffect(() => {
        // Don't overwrite optimistic state while mutation is in flight
        if (pendingProfileId) return

        const serverSelectedProfile = serverViewOptionProfiles?.find(profile => profile.selected)

        applyFilterProfile(serverSelectedProfile)
        setOptimisticallySelectedProfile(serverSelectedProfile)
    }, [serverViewOptionProfiles, applyFilterProfile, pendingProfileId])

    function setSelectedProfile(profile: Schemas['ViewOptionProfileOut'] | undefined) {
        const previousProfile = optimisticallySelectedProfile

        setOptimisticallySelectedProfile(profile ? { is_optimistic: true, ...profile } : undefined)
        setPendingProfileId(profile?.id ?? null)

        if (!profile) return

        selectViewOptionProfile.mutate(profile.id, {
            onSuccess: () => {
                setPendingProfileId(null)
            },
            onError: () => {
                setPendingProfileId(null)
                // Revert to previous state on error
                setOptimisticallySelectedProfile(previousProfile)
            },
        })
    }

    return {
        profiles: serverViewOptionProfiles ?? [],
        selectedProfile: optimisticallySelectedProfile,
        setSelectedProfile,
    }
}
